// Feedback dialog — captures category + message and either opens the user's
// mail client with a pre-filled email to support@zambahvac.com, or POSTs to
// /api/feedback if/when that backend endpoint exists. Today it falls back to
// mailto so engineers can ship feedback without us needing a server route.

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const CATEGORIES = [
  { value: "bug", label: "Bug report" },
  { value: "request", label: "Feature request" },
  { value: "ui", label: "UI / UX comment" },
  { value: "data", label: "Data correction (coil / model)" },
  { value: "other", label: "Other" },
];

export default function FeedbackDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("request");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = () => {
    if (!message.trim()) {
      toast.error("Please describe your feedback");
      return;
    }
    setSending(true);
    // mailto fallback — opens the user's default mail client. Long-term this
    // can be swapped for an /api/feedback endpoint that emails via Gmail SMTP
    // (already wired in `.env`).
    const subject = encodeURIComponent(`ZAMBA Selection Tool · ${CATEGORIES.find((c) => c.value === category)?.label || "Feedback"}`);
    const body = encodeURIComponent(
      [
        `From: ${user?.email || "(not signed in)"}`,
        `Category: ${category}`,
        `Page: ${window.location.href}`,
        `Browser: ${navigator.userAgent}`,
        ``,
        `--- Message ---`,
        message,
      ].join("\n")
    );
    window.location.href = `mailto:support@zambahvac.com?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSending(false);
      toast.success("Opening your mail client…");
      onOpenChange(false);
      setMessage("");
    }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> Send Feedback
          </DialogTitle>
          <DialogDescription>
            Bug reports, feature requests, or UI comments — they all help us
            improve the tool. The message will go to{" "}
            <span className="font-mono text-foreground">support@zambahvac.com</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-sm">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="feedback-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Message</Label>
            <Textarea
              className="mt-1 min-h-32"
              placeholder="What did you find? What were you trying to do?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="feedback-message"
              autoFocus
            />
          </div>
          <div className="text-[11px] text-muted-foreground">
            We'll automatically include your email ({user?.email || "—"}), the page URL,
            and browser info to help us reproduce the issue.
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={send}
            disabled={sending || !message.trim()}
            className="bg-foreground hover:bg-foreground/90 text-background"
            data-testid="feedback-send"
          >
            <Send className="h-4 w-4 mr-1" /> {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
